"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { PlusIcon, Cross2Icon, EnterFullScreenIcon } from "@radix-ui/react-icons";
import { apiFetch } from "@/lib/client-api";
import ResearchPilot from "./research-pilot";
import WorldModelLearning from "./world-model-learning";
import { engineNavigate } from "./use-decision-engine";
import WorldModelGraph from "./world-model-graph";
import WorldModelNodeIcon from "./world-model-node-icon";
import {
  claimExtractionFrequencies,
  claimExtractionFrequencyLabels,
  emptyWorkflowCanvas,
  type ClaimExtractionFrequency,
  type WorkflowCanvas,
  type WorkflowSourceNode,
} from "@/lib/workflow-canvas";
import {
  sourceAuthorityRelationshipLabels,
  sourceRelationshipLabel,
  type SourceAuthorityRelationship,
} from "@/lib/source-authority";

type EngineOption = {
  id: string;
  label: string;
  status?: string;
  kind?: string;
  url?: string;
  relationshipCount?: number;
  claimExtractionSupported?: boolean;
};

type PrimitiveKind = "sources" | "systems" | "timeline" | "simulations" | "portfolio";

type WorldModelWorkspaceProps = {
  engineOptions: {
    sources: EngineOption[];
    sourceRelationships: SourceAuthorityRelationship[];
    systems: EngineOption[];
    simulations: EngineOption[];
    positions: EngineOption[];
  };
  onOpenSources: () => void;
  onOpenSystems: () => void;
  onOpenTimeline: () => void;
  onOpenSimulations: () => void;
  onOpenPortfolio: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  graphFocusNodeId?: string | null;
  graphSnapshotId?: string | null;
  graphResearchCycle?: string | null;
  onOpenGraphRecord?: (view: string, targetId?: string) => void;
};

type WorkflowPayload = {
  canvas?: WorkflowCanvas;
  error?: string;
};

type SaveState = "idle" | "saving" | "saved" | "error";

const primitiveCopy: Record<PrimitiveKind, { label: string; description: string }> = {
  sources: {
    label: "Sources",
    description: "Add a source and control its claim-extraction cadence.",
  },
  systems: {
    label: "Systems",
    description: "System primitives are visible here and will become connectable next.",
  },
  timeline: {
    label: "Timeline",
    description: "Claim deadlines and observed events will become workflow inputs next.",
  },
  simulations: {
    label: "Simulation",
    description: "Simulation models will become runnable workflow steps next.",
  },
  portfolio: {
    label: "Portfolio",
    description: "Portfolio state will become an inspectable workflow output next.",
  },
};

function runtimeLabel() {
  if (typeof window === "undefined") return "ENV";
  const environment = window.__ASYMMETRIC_ENVIRONMENT__ ?? "test";
  return environment === "development" ? "DEV" : environment === "production" ? "PROD" : "TEST";
}

function sourceNode(option: EngineOption, index: number): WorkflowSourceNode {
  return {
    id: `workflow-node-${crypto.randomUUID()}`,
    kind: "source",
    sourceId: option.id,
    label: option.label,
    claimExtractionSupported: Boolean(option.claimExtractionSupported),
    enabled: Boolean(option.claimExtractionSupported),
    claimExtractionFrequency: "hourly",
    position: {
      x: 32 + (index % 2) * 360,
      y: 70 + Math.floor(index / 2) * 185,
    },
  };
}

function saveStatusLabel(
  state: SaveState,
  dirty: boolean,
  version: number,
  environment: string,
) {
  if (state === "saving") return `Saving to ${environment}`;
  if (state === "error") return "Save failed";
  if (dirty) return "Unsaved changes";
  if (state === "saved") return `Saved to ${environment}`;
  return version ? `Revision ${version} · ${environment}` : `New workflow · ${environment}`;
}

export default function WorldModelWorkspace({
  engineOptions,
  onOpenSources,
  onOpenSystems,
  onOpenTimeline,
  onOpenSimulations,
  onOpenPortfolio,
  onDirtyChange,
  graphFocusNodeId,
  graphSnapshotId,
  graphResearchCycle,
  onOpenGraphRecord,
}: WorldModelWorkspaceProps) {
  const [canvas, setCanvas] = useState<WorkflowCanvas>(emptyWorkflowCanvas());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [activePrimitive, setActivePrimitive] = useState<PrimitiveKind>("sources");
  const [screen, setScreen] = useState<"canvas" | "graph" | "workflow" | "sources" | "activity" | "research" | "learning" | "forecasts">(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("engine") === "learning" ? "learning" : "canvas");
  const [researchOpened, setResearchOpened] = useState(false);
  const [researchDirty, setResearchDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [sourceQuery, setSourceQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!graphFocusNodeId && !graphSnapshotId) return;
    const timer = window.setTimeout(() => setScreen("canvas"), 0);
    return () => window.clearTimeout(timer);
  }, [graphFocusNodeId, graphSnapshotId]);
  useEffect(() => { onDirtyChange?.(dirty || researchDirty); }, [dirty, researchDirty, onDirtyChange]);
  useEffect(() => () => { onDirtyChange?.(false); }, [onDirtyChange]);
  useEffect(() => {
    if (!dirty && !researchDirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, researchDirty]);
  const [environment, setEnvironment] = useState("ENV");
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    nodeId: string;
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  async function loadCanvas() {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await apiFetch("/api/workflow-canvas", {
        cache: "no-store",
        headers: { accept: "application/json" },
      });
      const payload = await response.json() as WorkflowPayload;
      if (!response.ok || !payload.canvas) {
        throw new Error(payload.error ?? "Workflow canvas could not be loaded.");
      }
      setCanvas(payload.canvas);
      setSelectedNodeId((current) =>
        payload.canvas?.nodes.some((node) => node.id === current)
          ? current
          : null);
      setDirty(false);
      setSaveState("idle");
      setSaveMessage("");
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Workflow canvas could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setEnvironment(runtimeLabel());
      void loadCanvas();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, []);

  const selectedNode = canvas.nodes.find((node) => node.id === selectedNodeId) ?? null;
  const sourceById = useMemo(
    () => new Map(engineOptions.sources.map((source) => [source.id, source])),
    [engineOptions.sources],
  );
  const canvasNodeBySourceId = useMemo(
    () => new Map(canvas.nodes.map((node) => [node.sourceId, node])),
    [canvas.nodes],
  );
  const visibleSourceRelationships = useMemo(
    () => engineOptions.sourceRelationships.filter((relationship) =>
      canvasNodeBySourceId.has(relationship.from) && canvasNodeBySourceId.has(relationship.to)),
    [canvasNodeBySourceId, engineOptions.sourceRelationships],
  );
  const selectedSourceRelationships = useMemo(
    () => selectedNode
      ? engineOptions.sourceRelationships.filter((relationship) =>
          relationship.from === selectedNode.sourceId || relationship.to === selectedNode.sourceId)
      : [],
    [engineOptions.sourceRelationships, selectedNode],
  );
  const canvasExtent = useMemo(() => ({
    width: Math.max(720, ...canvas.nodes.map((node) => node.position.x + 288)),
    height: Math.max(480, ...canvas.nodes.map((node) => node.position.y + 132)),
  }), [canvas.nodes]);
  const filteredSources = useMemo(() => {
    const existing = new Set(canvas.nodes.map((node) => node.sourceId));
    const query = sourceQuery.trim().toLowerCase();
    return engineOptions.sources
      .filter((source) => !existing.has(source.id))
      .filter((source) => !query || `${source.label} ${source.id} ${source.kind ?? ""}`.toLowerCase().includes(query))
      .sort((left, right) =>
        Number(Boolean(right.claimExtractionSupported)) - Number(Boolean(left.claimExtractionSupported))
        || (right.relationshipCount ?? 0) - (left.relationshipCount ?? 0)
        || left.label.localeCompare(right.label));
  }, [canvas.nodes, engineOptions.sources, sourceQuery]);

  const primitiveCounts: Record<PrimitiveKind, number> = {
    sources: engineOptions.sources.length,
    systems: engineOptions.systems.length,
    timeline: 2,
    simulations: engineOptions.simulations.length,
    portfolio: engineOptions.positions.length,
  };

  const openPrimitive: Record<PrimitiveKind, () => void> = {
    sources: onOpenSources,
    systems: onOpenSystems,
    timeline: onOpenTimeline,
    simulations: onOpenSimulations,
    portfolio: onOpenPortfolio,
  };

  function updateNodes(update: (nodes: WorkflowSourceNode[]) => WorkflowSourceNode[]) {
    setCanvas((current) => ({ ...current, nodes: update(current.nodes) }));
    setDirty(true);
    setSaveState("idle");
    setSaveMessage("");
  }

  function addSource(option: EngineOption) {
    const node = sourceNode(option, canvas.nodes.length);
    updateNodes((nodes) => [...nodes, node]);
    setSelectedNodeId(node.id);
    setPickerOpen(false);
    setScreen("workflow");
  }

  function revealRelatedSource(sourceId: string) {
    const existing = canvasNodeBySourceId.get(sourceId);
    if (existing) {
      setSelectedNodeId(existing.id);
      return;
    }
    const option = sourceById.get(sourceId);
    if (option) addSource(option);
  }

  function updateSelectedNode(update: Partial<WorkflowSourceNode>) {
    if (!selectedNodeId) return;
    updateNodes((nodes) => nodes.map((node) =>
      node.id === selectedNodeId ? { ...node, ...update } : node));
  }

  function moveNode(nodeId: string, x: number, y: number) {
    updateNodes((nodes) => nodes.map((node) => node.id === nodeId
      ? { ...node, position: { x: Math.max(0, x), y: Math.max(0, y) } }
      : node));
  }

  function removeSelectedNode() {
    if (!selectedNodeId) return;
    const index = canvas.nodes.findIndex((node) => node.id === selectedNodeId);
    const remaining = canvas.nodes.filter((node) => node.id !== selectedNodeId);
    updateNodes(() => remaining);
    setSelectedNodeId(remaining[index]?.id ?? remaining[index - 1]?.id ?? null);
  }

  async function saveCanvas() {
    setSaveState("saving");
    setSaveMessage("");
    try {
      const response = await apiFetch("/api/workflow-canvas", {
        method: "PUT",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({
          expectedVersion: canvas.version,
          input: { nodes: canvas.nodes },
        }),
      });
      const payload = await response.json() as WorkflowPayload;
      if (!response.ok || !payload.canvas) {
        throw new Error(payload.error ?? "Workflow canvas could not be saved.");
      }
      setCanvas(payload.canvas);
      setDirty(false);
      setSaveState("saved");
      setSaveMessage(`Workflow saved in ${environment}.`);
    } catch (error) {
      setSaveState("error");
      setSaveMessage(error instanceof Error ? error.message : "Workflow canvas could not be saved.");
    }
  }

  function startDrag(event: PointerEvent<HTMLButtonElement>, node: WorkflowSourceNode) {
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setSelectedNodeId(node.id);
    dragRef.current = {
      nodeId: node.id,
      pointerId: event.pointerId,
      offsetX: (event.clientX - bounds.left + (canvasRef.current?.scrollLeft ?? 0)) / zoom - node.position.x,
      offsetY: (event.clientY - bounds.top + (canvasRef.current?.scrollTop ?? 0)) / zoom - node.position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function dragNode(event: PointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!drag || !bounds) return;
    const x = Math.round(Math.max(0, (event.clientX - bounds.left + (canvasRef.current?.scrollLeft ?? 0)) / zoom - drag.offsetX));
    const y = Math.round(Math.max(0, (event.clientY - bounds.top + (canvasRef.current?.scrollTop ?? 0)) / zoom - drag.offsetY));
    moveNode(drag.nodeId, x, y);
  }

  function finishDrag(event: PointerEvent<HTMLButtonElement>) {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function moveNodeWithKeyboard(event: KeyboardEvent<HTMLButtonElement>, node: WorkflowSourceNode) {
    const delta = event.shiftKey ? 24 : 8;
    const movement = {
      ArrowLeft: [-delta, 0],
      ArrowRight: [delta, 0],
      ArrowUp: [0, -delta],
      ArrowDown: [0, delta],
    }[event.key];
    if (!movement) return;
    event.preventDefault();
    setSelectedNodeId(node.id);
    updateNodes((nodes) => nodes.map((candidate) => candidate.id === node.id
      ? {
          ...candidate,
          position: {
            x: Math.max(0, candidate.position.x + movement[0]),
            y: Math.max(0, candidate.position.y + movement[1]),
          },
        }
      : candidate));
  }

  return (
    <section className="workspace workflow-workspace" id="workspace" aria-labelledby="world-model-title">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Composition</p>
          <h1 id="world-model-title">World Model</h1>
          {screen !== "learning" && screen !== "forecasts" && <p className="lede">{(screen === "graph" || screen === "canvas") ? "Your connected view of the world" : "AI research workflow"}</p>}
        </div>
        {screen !== "graph" && screen !== "canvas" && screen !== "research" && screen !== "learning" && screen !== "forecasts" &&
        <div className="connection-summary workflow-connection-summary" aria-live="polite">
          <strong>
            {canvas.nodes.length} source {canvas.nodes.length === 1 ? "node" : "nodes"}
            {visibleSourceRelationships.length ? ` · ${visibleSourceRelationships.length} canonical ${visibleSourceRelationships.length === 1 ? "link" : "links"}` : ""}
          </strong>
          <span>{saveStatusLabel(saveState, dirty, canvas.version, environment)}</span>
          <button type="button" disabled={loading || saveState === "saving" || !dirty} onClick={() => void saveCanvas()}>
            {saveState === "saving" ? "Saving" : "Save workflow"}
          </button>
        </div>}
      </header>

      {loadError && screen !== "graph" && screen !== "canvas" && screen !== "research" && screen !== "learning" && screen !== "forecasts" && (
        <div className="service-error workflow-error" role="alert">
          <span>{loadError}</span>
          <button type="button" onClick={() => void loadCanvas()}>Try again</button>
        </div>
      )}

      <nav className="quiet-tabs" aria-label="World Model views">{(["canvas", "graph", "forecasts", "learning", "sources", "activity", "research"] as const).map(tab => <button type="button" key={tab} aria-pressed={screen === tab || (screen === "workflow" && tab === "sources")} onClick={() => { setScreen(tab); if (tab === "research") setResearchOpened(true); }}>{tab === "research" ? "Research pilot" : tab[0].toUpperCase() + tab.slice(1)}</button>)}</nav>
      {(screen === "learning" || screen === "forecasts") && <WorldModelLearning forecastsOnly={screen === "forecasts"}/>}
      <div hidden={screen !== "graph" && screen !== "canvas"}><WorldModelGraph view={screen === "graph" ? "graph" : "canvas"} onViewChange={setScreen} onConfigureSources={() => { setScreen("workflow"); setPickerOpen(true); }} focusNodeId={graphFocusNodeId} snapshotId={graphSnapshotId} researchCycle={graphResearchCycle} navigationLabel={onOpenGraphRecord ? "Open record" : "Open workspace"} onNavigate={(view, targetId) => {
        if (view === "engine-learning") { setScreen("learning"); }
        else if (view === "engine-simulation") engineNavigate("simulation", targetId);
        else if (view === "engine-decision") engineNavigate("decision", targetId);
        else if (view === "research") { setResearchOpened(true); setScreen("research"); }
        else if (onOpenGraphRecord) onOpenGraphRecord(view, targetId);
        else if (view === "sources") onOpenSources();
        else if (view === "systems") onOpenSystems();
        else if (view === "timeline") onOpenTimeline();
        else if (view === "simulations") onOpenSimulations();
        else if (view === "portfolio") onOpenPortfolio();
        else if (view === "world-model") setScreen("canvas");
      }} /></div>
      {researchOpened && <div hidden={screen !== "research"}><ResearchPilot onDirtyChange={setResearchDirty} /></div>}
      {screen === "sources" && <section className="workflow-saved-sources"><h2>Workflow sources</h2><button type="button" onClick={() => { setScreen("workflow"); setPickerOpen(true); }}>Add source</button><button type="button" onClick={() => setScreen("workflow")}>Arrange sources & settings</button>{canvas.nodes.length ? canvas.nodes.map(node => <button type="button" key={node.id} onClick={() => { setSelectedNodeId(node.id); setScreen("workflow"); }}><strong>{node.label}</strong><span>{node.claimExtractionSupported ? node.enabled ? claimExtractionFrequencyLabels[node.claimExtractionFrequency] : "Paused" : "Registered"}</span><span aria-hidden="true">→</span></button>) : <p className="muted">Add a source to begin your workflow.</p>}</section>}
      {screen === "activity" && <section className="workflow-activity"><h2>Workflow activity</h2><p>{saveStatusLabel(saveState, dirty, canvas.version, environment)}</p><p>{canvas.nodes.length} sources · {visibleSourceRelationships.length} relationships</p><p className="muted">{saveMessage || "Saved source settings determine when each eligible source is processed."}</p></section>}
      <div className="workflow-shell" hidden={screen !== "workflow"} aria-busy={loading} data-screen={screen} data-inspector={Boolean(selectedNode)} inert={saveState === "saving" || loading}>
        {screen === "workflow" && <button className="workflow-add-source" type="button" aria-expanded={pickerOpen} onClick={() => { setActivePrimitive("sources"); setPickerOpen(value => !value); }}><PlusIcon /> Add source</button>}
        {pickerOpen && <button className="workflow-picker-dismiss" type="button" aria-label="Close source picker" onClick={() => setPickerOpen(false)} />}
        {pickerOpen &&
        <aside className="workflow-primitives" aria-label="Workflow primitives">
          <header>
            <strong>Add a source</strong><button className="icon-button" type="button" aria-label="Close source picker" onClick={() => setPickerOpen(false)}><Cross2Icon /></button>
          </header>
          <nav aria-label="Primitive categories">
            {(Object.keys(primitiveCopy) as PrimitiveKind[]).map((kind) => (
              <button
                type="button"
                aria-pressed={activePrimitive === kind}
                key={kind}
                onClick={() => setActivePrimitive(kind)}
              >
                <span>{primitiveCopy[kind].label}</span>
                <small>{primitiveCounts[kind]}</small>
              </button>
            ))}
          </nav>

          <section className="workflow-primitive-library" data-primitive={activePrimitive} aria-label={`${primitiveCopy[activePrimitive].label} library`}>
            <div>
              <strong>{primitiveCopy[activePrimitive].label}</strong>
              <button type="button" onClick={openPrimitive[activePrimitive]}>Open category</button>
            </div>
            <p>{primitiveCopy[activePrimitive].description}</p>

            {activePrimitive === "sources" ? (
              <>
                <label className="workflow-source-search">
                  <span>Filter sources</span>
                  <input
                    type="search" aria-label="Find a workflow source"
                    value={sourceQuery}
                    onChange={(event) => setSourceQuery(event.target.value)}
                    placeholder="Search"
                  />
                </label>
                <div className="workflow-source-list">
                  {filteredSources.map((source) => (
                    <button type="button" key={source.id} onClick={() => addSource(source)}>
                      <WorldModelNodeIcon source={source} /><span>
                        <strong>{source.label}</strong>
                        <small>
                          {source.kind ?? "Source"} · {source.relationshipCount ?? 0} {(source.relationshipCount ?? 0) === 1 ? "link" : "links"}
                          {source.claimExtractionSupported ? " · claim extraction" : ""}
                        </small>
                      </span>
                      <i aria-hidden="true">Add</i>
                    </button>
                  ))}
                  {!filteredSources.length && (
                    <p>{sourceQuery ? "No matching sources." : "Every source is already on the canvas."}</p>
                  )}
                </div>
              </>
            ) : (
              <div className="workflow-coming-next">
                <span>Visible now</span>
                <strong>Connectable next</strong>
              </div>
            )}
          </section>
        </aside>}

        <section className="workflow-canvas-panel" aria-label="Workflow canvas">
          <header>
            <div>
              <strong>Canvas</strong>
              <span>Source layer</span>
            </div>
            <small>{canvas.nodes.length ? "Drag nodes to arrange" : "Ready for a source"}</small>
          </header>
          <div className="workflow-canvas" ref={canvasRef}>
            <div className="workflow-stage-space" style={{ width: canvasExtent.width * zoom, height: canvasExtent.height * zoom }}><div className="workflow-stage" style={{ width: canvasExtent.width, height: canvasExtent.height, transform: `scale(${zoom})` }}>
            <svg
              className="workflow-source-links"
              width={canvasExtent.width}
              height={canvasExtent.height}
              aria-label={`${visibleSourceRelationships.length} canonical source relationships on the canvas`}
            >
              <defs>
                <marker
                  id="workflow-source-arrow"
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="5"
                  markerHeight="5"
                  orient="auto-start-reverse"
                >
                  <path className="workflow-source-arrowhead" d="M 0 0 L 10 5 L 0 10 z" />
                </marker>
              </defs>
              {visibleSourceRelationships.map((relationship) => {
                const fromNode = canvasNodeBySourceId.get(relationship.from);
                const toNode = canvasNodeBySourceId.get(relationship.to);
                const fromSource = sourceById.get(relationship.from);
                const toSource = sourceById.get(relationship.to);
                if (!fromNode || !toNode || !fromSource || !toSource) return null;
                const leftToRight = toNode.position.x + 94 >= fromNode.position.x + 94;
                const x1 = fromNode.position.x + (leftToRight ? 240 : 0);
                const x2 = toNode.position.x + (leftToRight ? 0 : 240);
                const y1 = fromNode.position.y + 40;
                const y2 = toNode.position.y + 40;
                const detour = Math.abs(x2 - x1) > 330 && Math.abs(y2 - y1) < 70;
                const path = detour ? `M ${x1} ${y1} C ${x1 + (x2-x1)*.25} ${y1-120}, ${x2 - (x2-x1)*.25} ${y2-120}, ${x2} ${y2}` : `M ${x1} ${y1} L ${x2} ${y2}`;
                return (
                  <g key={relationship.id}>
                    <title>
                      {fromSource.label} {sourceAuthorityRelationshipLabels[relationship.kind].outbound.toLowerCase()} {toSource.label}
                    </title>
                    <path className="workflow-source-edge" d={path} markerEnd="url(#workflow-source-arrow)" />

                  </g>
                );
              })}
            </svg>
            {!loading && !canvas.nodes.length && (
              <div className="workflow-canvas-empty">
                <strong>Start with a source.</strong><button type="button" onClick={() => setPickerOpen(true)}>Add source</button>
                <span>Add a source to connect your research.</span>
              </div>
            )}
            {canvas.nodes.map((node) => {
              const source = sourceById.get(node.sourceId);
              return (
                <article
                  className="workflow-source-node"
                  data-selected={node.id === selectedNodeId ? "true" : "false"}
                  key={node.id}
                  style={{ transform: `translate(${node.position.x}px, ${node.position.y}px)` }}
                >
                  <button
                    type="button"
                    aria-label={`${node.label} source node. Use arrow keys to move.`}
                    aria-pressed={node.id === selectedNodeId}
                    onClick={() => setSelectedNodeId(node.id)}
                    onKeyDown={(event) => moveNodeWithKeyboard(event, node)}
                    onPointerDown={(event) => startDrag(event, node)}
                    onPointerMove={dragNode}
                    onPointerUp={finishDrag}
                    onPointerCancel={finishDrag}
                  >
                    <WorldModelNodeIcon source={source ?? { id: node.sourceId, label: node.label }} className="workflow-node-icon" /><span>{source?.kind ?? "Source"}</span>
                    <strong>{node.label}</strong>
                    <small>
                      {node.claimExtractionSupported
                        ? node.enabled
                          ? claimExtractionFrequencyLabels[node.claimExtractionFrequency]
                          : "Extraction paused"
                        : source?.status ?? "Registered"}
                    </small>
                  </button>
                </article>
              );
            })}
            </div></div>
          </div>
        </section>

        {selectedNode && <aside className="workflow-inspector" aria-label="Node settings">
          <header>
            <strong>Source</strong><button type="button" className="icon-button" aria-label="Close source inspector" onClick={() => setSelectedNodeId(null)}><Cross2Icon /></button>
          </header>
          {selectedNode ? (
            <div className="workflow-inspector-body">
              <div className="workflow-node-identity">
                <span>{sourceById.get(selectedNode.sourceId)?.kind ?? "Source"}</span>
                <strong>{selectedNode.label}</strong>
                <small>{selectedNode.sourceId}</small>
              </div>

              {selectedSourceRelationships.length ? (
                <section className="workflow-source-relations" aria-label="Canonical source relationships">
                  <header>
                    <span>Canonical relationships</span>
                    <small>{selectedSourceRelationships.length} registered</small>
                  </header>
                  <div>
                    {selectedSourceRelationships.map((relationship) => {
                      const otherId = relationship.from === selectedNode.sourceId
                        ? relationship.to
                        : relationship.from;
                      const other = sourceById.get(otherId);
                      if (!other) return null;
                      const onCanvas = canvasNodeBySourceId.has(otherId);
                      return (
                        <button
                          type="button"
                          key={relationship.id}
                          onClick={() => revealRelatedSource(otherId)}
                        >
                          <span>{sourceRelationshipLabel(relationship, selectedNode.sourceId)}</span>
                          <strong>{other.label}</strong>
                          <small>
                            {relationship.confidence} confidence · {onCanvas ? "select node" : "add node"}
                          </small>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <div className="workflow-toggle-row">
                <span>
                  <strong>Claim extraction</strong>
                  <small>{selectedNode.claimExtractionSupported ? "Scheduler-controlled" : "Unavailable for this source"}</small>
                </span>
                <input
                  type="checkbox"
                  aria-label="Claim extraction"
                  role="switch"
                  className="quiet-toggle"
                  checked={selectedNode.enabled}
                  disabled={!selectedNode.claimExtractionSupported}
                  onChange={(event) => updateSelectedNode({ enabled: event.target.checked })}
                />
              </div>

              <label className="workflow-field">
                <span>Frequency</span>
                <select
                  value={selectedNode.claimExtractionFrequency}
                  disabled={!selectedNode.claimExtractionSupported || !selectedNode.enabled}
                  onChange={(event) => updateSelectedNode({
                    claimExtractionFrequency: event.target.value as ClaimExtractionFrequency,
                  })}
                >
                  {claimExtractionFrequencies.map((frequency) => (
                    <option value={frequency} key={frequency}>{claimExtractionFrequencyLabels[frequency]}</option>
                  ))}
                </select>
                <small>
                  {selectedNode.claimExtractionSupported
                    ? "The 15-minute scheduler will only discover and process this source when its saved cadence is due."
                    : "This registered source does not currently expose the transcript pipeline."}
                </small>
              </label>

              <div className="workflow-position-readout">
                <span>Canvas position</span>
                <strong>{selectedNode.position.x} × {selectedNode.position.y}</strong>
              </div>

              <button className="workflow-remove-node" type="button" onClick={removeSelectedNode}>
                Remove from canvas
              </button>
            </div>
          ) : (
            <div className="workflow-inspector-empty">
              <strong>Select a node.</strong>
              <span>Its source controls will appear here.</span>
            </div>
          )}
        </aside>}
      </div>
      {screen === "workflow" && <footer className="workflow-footer"><p className="muted">{canvas.nodes.length} sources · Relationships from source records</p><div><nav className="quiet-tabs" aria-label="Primitive categories">{(Object.keys(primitiveCopy) as PrimitiveKind[]).map(kind => <button key={kind} type="button" aria-pressed={activePrimitive === kind} onClick={() => { setActivePrimitive(kind); if (kind !== "sources") setPickerOpen(true); }}>{primitiveCopy[kind].label}</button>)}</nav><div className="workflow-view-controls"><button type="button" onClick={() => { const rect = canvasRef.current?.getBoundingClientRect(); if (rect) setZoom(Math.min(1, (rect.width - 24) / canvasExtent.width, (rect.height - 24) / canvasExtent.height)); canvasRef.current?.scrollTo({ left: 0, top: 0 }); }}><EnterFullScreenIcon /> Fit view</button><select aria-label="Canvas zoom" value={zoom} onChange={event => setZoom(Number(event.target.value))}>{![.5, .75, 1, 1.25, 1.5].includes(zoom) && <option value={zoom}>{Math.round(zoom * 100)}%</option>}{[.5, .75, 1, 1.25, 1.5].map(value => <option key={value} value={value}>{value * 100}%</option>)}</select></div></div><small>More node types coming later</small></footer>}
      <p className="boundary workflow-boundary" hidden={screen === "graph" || screen === "canvas" || screen === "research"} role={saveState === "error" ? "alert" : "status"}>
        {saveMessage || ""}
      </p>
    </section>
  );
}
